import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, FileJson, RotateCcw, Upload } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { CorrespJson } from "./CorrespJson";
import { Confirmer } from "./Confirmer";
import { Barre } from "@/components/tableaux/commun";
import { RapportJson } from "./RapportJson";

const MORCEAU = 2 * 1024 * 1024;
const Mo = (o) => `${(o / 1048576).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`;

const Depot = ({ e, onMaj }) => {
  const ref = useRef(null);
  const [envoi, setEnvoi] = useState(null);
  const deposer = async (fichier) => {
    if (!fichier) return;
    try {
      const { data } = await api.post("/migration-nocrm/json/fichier", { nom: fichier.name, taille: fichier.size });
      const n = Math.ceil(fichier.size / MORCEAU);
      for (let i = 0; i < n; i += 1) {
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("morceau", fichier.slice(i * MORCEAU, (i + 1) * MORCEAU), "morceau.bin");
        await api.post(`/migration-nocrm/json/fichier/${data.id}/morceau`, fd);
        setEnvoi(Math.round((100 * (i + 1)) / n));
      }
      await api.post(`/migration-nocrm/json/fichier/${data.id}/terminer`);
      toast.success("Fichier déposé : analyse en cours.");
      onMaj();
    } catch (x) { toast.error(messageErreur(x)); } finally { setEnvoi(null); if (ref.current) ref.current.value = ""; }
  };
  const f = e.fichier;
  return (
    <section className="at-card p-5 space-y-3" data-testid="json-upload">
      <p className="font-bold flex items-center gap-2"><FileJson size={18} />Fichier d'export JSON noCRM</p>
      {f && <p className="text-sm break-words" data-testid="json-file-info">{f.nom} · {Mo(f.taille)} · {{ televersement: "dépôt interrompu", analyse: "analyse en cours", pret: "prêt", erreur: "erreur" }[f.statut]}</p>}
      {f?.statut === "erreur" && <p className="text-sm" style={{ color: "var(--danger)" }} data-testid="json-file-error">{f.erreur}</p>}
      {(envoi !== null || f?.statut === "analyse") && (
        <div className="space-y-1" data-testid="json-upload-progress"><p className="text-xs at-muted">{envoi !== null ? `Envoi ${envoi} %` : `Analyse ${f.progression} %`}</p><Barre pct={envoi ?? f.progression} /></div>
      )}
      {e.resume && <p className="text-sm" data-testid="json-file-summary">{e.resume.opportunites.toLocaleString("fr-FR")} opportunités · {e.resume.commentaires.toLocaleString("fr-FR")} commentaires (jusqu'à {e.resume.max_commentaires} sur une fiche)</p>}
      <input ref={ref} type="file" accept=".json,application/json" className="hidden" onChange={(x) => deposer(x.target.files[0])} data-testid="json-file-input" />
      <button type="button" disabled={envoi !== null || f?.statut === "analyse"} onClick={() => ref.current?.click()} className="at-btn at-btn-ghost w-full" data-testid="json-upload-button">
        <Upload size={16} />{f ? "Déposer un autre fichier JSON" : "Déposer le fichier JSON"}</button>
      <p className="text-xs at-muted">Envoi par morceaux de 2 Mo, conservé dans le stockage de l'app pour relancer l'import sans le redéposer.</p>
    </section>
  );
};

const Etape = ({ n, titre, children, testId }) => (
  <section className="at-card p-5 space-y-3" data-testid={testId}>
    <p className="font-bold flex items-center gap-2"><span className="w-7 h-7 at-pill flex items-center justify-center text-sm" style={{ background: "var(--accent)", color: "var(--accent-ink)" }}>{n}</span>{titre}</p>
    {children}
  </section>
);

export const ImportJson = () => {
  const [e, setE] = useState(null);
  const charger = useCallback(() => api.get("/migration-nocrm/json").then((r) => setE(r.data)).catch((x) => toast.error(messageErreur(x))), []);
  useEffect(() => { charger(); }, [charger]);
  const actif = e && (e.fichier?.statut === "analyse" || e.job?.statut === "en_cours");
  useEffect(() => { if (!actif) return undefined; const t = setInterval(charger, 2000); return () => clearInterval(t); }, [actif, charger]);
  const lancer = async (url, ok) => { try { const r = await api.post(url); toast.success(r.data.message || ok); charger(); } catch (x) { toast.error(messageErreur(x)); } };
  const telecharger = async () => {
    try {
      const r = await api.get("/migration-nocrm/json/rapport.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "rapport-import-nocrm.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (x) { toast.error(messageErreur(x)); }
  };
  if (!e) return <p className="at-muted text-sm">Chargement…</p>;
  const pret = e.fichier?.statut === "pret";
  const enCours = e.job?.statut === "en_cours";
  return (
    <div className="space-y-4" data-testid="json-import">
      <Depot e={e} onMaj={charger} />
      {pret && (
        <>
          <Etape n="1" titre="Correspondances" testId="json-step-1"><CorrespJson key={e.fichier.id} e={e} onMaj={setE} /></Etape>
          <Etape n="2" titre="Échantillon de 50 fiches" testId="json-step-2">
            <button type="button" disabled={enCours || e.manquantes > 0 || e.etat === "complet"} onClick={() => lancer("/migration-nocrm/json/echantillon", "Import de l'échantillon lancé.")} className="at-btn at-btn-primary w-full" data-testid="json-sample-button">Importer l'échantillon<ArrowRight size={16} /></button>
            {e.etat === "echantillon" && !enCours && <Confirmer titre="Annuler l'échantillon ?" texte="Les fiches de l'échantillon partent à la corbeille (restaurables 30 jours)." testId="json-cancel-sample" onOk={() => lancer("/migration-nocrm/json/annuler-echantillon")}><RotateCcw size={16} />Annuler l'échantillon</Confirmer>}
          </Etape>
          <Etape n="3" titre="Import complet" testId="json-step-3">
            <p className="text-xs at-muted">Tâche de fond, relançable sans doublon (identifiant noCRM). Étape et agent modifiés dans le CRM conservés, seuls les nouveaux commentaires ajoutés. Aucune automatisation ni aucun message envoyé.</p>
            <button type="button" disabled={enCours || e.manquantes > 0 || e.etat === "a_configurer"} onClick={() => lancer("/migration-nocrm/json/complet", "Import complet lancé.")} className="at-btn at-btn-primary w-full" data-testid="json-full-button">{e.etat === "complet" ? "Relancer l'import" : "Lancer l'import complet"}<ArrowRight size={16} /></button>
            {e.importees > 0 && !enCours && <Confirmer danger titre="Annuler l'import complet ?" texte={`Les ${e.importees} fiches importées du fichier JSON partent à la corbeille (restaurables 30 jours).`} testId="json-cancel-full" onOk={() => lancer("/migration-nocrm/json/annuler-import")}><RotateCcw size={16} />Annuler l'import complet</Confirmer>}
          </Etape>
        </>
      )}
      {e.job && (
        <section className="at-card p-5 space-y-2" data-testid="json-job">
          <p className="font-semibold text-sm">{enCours ? "Import en cours…" : e.job.statut === "erreur" ? "Import interrompu" : "Dernier import terminé"} ({e.job.lot === "echantillon" ? "échantillon" : "complet"})</p>
          <Barre pct={e.job.total ? (100 * e.job.traites) / e.job.total : enCours ? 2 : 100} />
          <p className="text-xs at-muted" data-testid="json-job-progress">{e.job.traites} / {e.job.total || "…"} opportunité(s){e.job.erreur ? ` · ${e.job.erreur}` : ""}</p>
        </section>
      )}
      {e.rapport && !enCours && <RapportJson r={e.rapport} onCsv={telecharger} />}
    </div>
  );
};
